import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-helpers";
import { LayoutGrid, Rows3 } from "lucide-react";

const MODULES = ["Combined_Leads", "DQ Leads", "Clients", "Contacts", "Proposals", "Tranches"];
const STAGES = ["New", "Meet I booked", "Proposed", "Meet II", "Meet III", "Won", "Lost"];

export default function Pipeline() {
  const [module, setModule] = useState("Combined_Leads");
  const [view, setView] = useState("board");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/pipeline", { params: { module, view } }).then((r) => setData(r.data));
  }, [module, view]);

  return (
    <div data-testid="pipeline-page">
      <PageHeader
        title="Pipeline"
        sub="Mirror of Zoho CRM · read-only in Phase 1"
        right={
          <>
            <div className="hidden md:flex items-center gap-1 panel p-0.5 rounded">
              <button
                data-testid="view-board"
                onClick={() => setView("board")}
                className={`px-2 py-1 text-xs rounded ${view === "board" ? "bg-[var(--panel-2)] text-white" : "text-neutral-500"}`}
              >
                <LayoutGrid size={12} className="inline mr-1" /> Board
              </button>
              <button
                data-testid="view-table"
                onClick={() => setView("table")}
                className={`px-2 py-1 text-xs rounded ${view === "table" ? "bg-[var(--panel-2)] text-white" : "text-neutral-500"}`}
              >
                <Rows3 size={12} className="inline mr-1" /> Table
              </button>
            </div>
          </>
        }
      />

      <div className="px-4 md:px-6 py-3 border-b border-[var(--border)] flex gap-1 overflow-x-auto" data-testid="module-tabs">
        {MODULES.map((m) => (
          <button
            key={m}
            data-testid={`module-${m}`}
            onClick={() => setModule(m)}
            className={`px-3 py-1 text-xs rounded whitespace-nowrap ${module === m ? "bg-[var(--panel-2)] text-white border border-[var(--border-strong)]" : "text-neutral-500 hover:text-neutral-300"}`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="p-4 md:p-6">
        {!data ? (
          <div className="text-xs text-neutral-500">Loading…</div>
        ) : view === "board" ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {STAGES.map((s) => (
              <div key={s} className="panel p-3 min-h-[200px]" data-testid={`stage-${s}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-neutral-500">{s}</span>
                  <span className="text-[11px] font-mono text-neutral-400">{data.board[s]?.length || 0}</span>
                </div>
                {(!data.board[s] || data.board[s].length === 0) && (
                  <div className="text-[11px] text-neutral-700 text-center py-6">—</div>
                )}
                {data.board[s]?.map((r) => (
                  <div key={r.zoho_id} className="panel-2 p-2 mb-2">
                    <div className="text-xs truncate">{r.name}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      {r.gmv ? `₹${r.gmv.toLocaleString("en-IN")}` : "GMV —"}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="panel overflow-x-auto">
            {data.rows.length === 0 ? (
              <Empty label="No leads cached. Connect Zoho CRM in Settings → Integrations." />
            ) : (
              <table className="table-dense w-full">
                <thead className="border-b border-[var(--border)]">
                  <tr className="text-left">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Stage</th>
                    <th className="px-3 py-2">Owner</th>
                    <th className="px-3 py-2">GMV</th>
                    <th className="px-3 py-2">Event date</th>
                    <th className="px-3 py-2">Qualified</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.zoho_id} className="border-b border-[var(--border)]">
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2 text-neutral-400">{r.stage}</td>
                      <td className="px-3 py-2 text-neutral-400">{r.owner}</td>
                      <td className="px-3 py-2 font-mono">{r.gmv || "—"}</td>
                      <td className="px-3 py-2 text-neutral-400">{r.event_date || "—"}</td>
                      <td className="px-3 py-2">{r.qualified_lead1 || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
